import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { MessageService } from "primeng/api";
import { catchError, map, mergeMap, of } from "rxjs";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { AgentActions } from "../actions/agent.actions";

@Injectable()
export class AgentEffects {
  private messageService = inject(MessageService);

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) {}

  // loading agents
  loadAgents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentActions.loadAgent),
      mergeMap((action) =>
        this.restService.getAgents(action.projectId).pipe(
          map((response) =>
            AgentActions.loadAgentSuccess({ agentList: response.data })
          ),
          catchError((error) =>
            of(AgentActions.loadAgentFailure({ error: error.message }))
          )
        )
      )
    )
  );

  // create agent
  createAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentActions.createAgent),
      mergeMap((action) =>
        this.restService.createAgent(action.projectId, action.data).pipe(
          map((agent) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.AGENT_CREATION_SUCCESS});
            return AgentActions.createAgentSuccess({ agent: agent.data });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_CREATION_FAILURE});
            return of(
              AgentActions.createAgentFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  // update agent
  updateAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentActions.updateAgent),
      mergeMap((action) =>
        this.restService
          .updateAgent(action.projectId, action.agent.id, action.agent)
          .pipe(
            map((agent) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AGENT_UPDATE_SUCCESS});
              return AgentActions.updateAgentSuccess({ agent: agent });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_UPDATE_FAILURE});
              return of(
                AgentActions.updateAgentFailure({ error: error.message })
              );
            })
          )
      )
    )
  );

  // delete agent
  deleteAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentActions.deleteAgent),
      mergeMap((action) =>
        this.restService.deleteAgent(action.projectId, action.agentIdList).pipe(
          map(() => {
            this.toaster.pop({ type: 'success', text: MessageEnum.AGENT_DELETION_SUCCESS});
            return AgentActions.deleteAgentSuccess({
              agentIdList: action.agentIdList,
            });
          }),
          catchError((error) => {
            this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_DELETION_FAILURE});
            return of(
              AgentActions.deleteAgentFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  // reclaim agent
  reclaimAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentActions.reclaimAgent),
      mergeMap((action) =>
        this.restService
          .reclaimAgent(action.projectId, action.agentIdList)
          .pipe(
            map((agents) => {
              this.toaster.pop({ type: 'success', text: MessageEnum.AGENT_RECLAIM_SUCCESS});
              return AgentActions.reclaimAgentSuccess({ agents: agents });
            }),
            catchError((error) => {
              this.toaster.pop({ type: 'error', text: MessageEnum.AGENT_RECLAIM_FAILURE});
              return of(
                AgentActions.reclaimAgentFailure({ error: error.message })
              );
            })
          )
      )
    )
  );
}
